import React from "react";
import { CalendarDays, GanttChart, Kanban, LayoutList, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "../UI/Button";
import { Input } from "../UI/FormField";

type Tab = "all" | "todo" | "sales";
export type ViewMode = "table" | "kanban" | "calendar" | "gantt";

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  onNewTask: () => void;
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  filtersSlot?: React.ReactNode;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

const TABS: Tab[] = ["all", "todo", "sales"];

const VIEW_MODES = [
  { mode: "table" as ViewMode, Icon: LayoutList, titleKey: "kanban.listView" },
  { mode: "kanban" as ViewMode, Icon: Kanban, titleKey: "kanban.kanbanView" },
  { mode: "calendar" as ViewMode, Icon: CalendarDays, titleKey: "calendar.viewLabel" },
  { mode: "gantt" as ViewMode, Icon: GanttChart, titleKey: "gantt.viewLabel" },
] as const;

export const TaskListHeader: React.FC<Props> = ({
  search,
  onSearchChange,
  onNewTask,
  activeTab,
  onTabChange,
  filtersSlot,
  viewMode = "table",
  onViewModeChange,
}) => {
  const { t } = useTranslation("tasks");

  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-dark-900 mb-4">
        {t("module.title")}
      </h1>

      {/*
        Mobile  (<640px): flex-col  — search on row 1, actions on row 2.
        Desktop (≥640px): flex-row  — search left, actions right.

        Actions use flex-wrap so that on very small phones (≤360px) the
        "New Task" button wraps to its own full-width line when there
        isn't enough room. flex-1 on the button makes it fill that line.
      */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-4">

        {/* Search — full width on mobile, max 320px on desktop */}
        <div className="relative w-full sm:flex-1 sm:min-w-0 sm:max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            type="text"
            placeholder={t("list.searchPlaceholder")}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 bg-white w-full"
          />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0">

          {/* View mode toggle — 4 icon buttons */}
          {onViewModeChange && (
            <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden flex-shrink-0">
              {VIEW_MODES.map(({ mode, Icon, titleKey }, idx) => (
                <button
                  key={mode}
                  type="button"
                  title={t(titleKey)}
                  onClick={() => onViewModeChange(mode)}
                  className={`px-2.5 py-1.5 transition-colors ${
                    idx > 0 ? "border-l border-gray-300" : ""
                  } ${
                    viewMode === mode
                      ? "bg-primary text-white"
                      : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          )}

          {/* Filters slot */}
          {filtersSlot && (
            <div className="flex-shrink-0">{filtersSlot}</div>
          )}

          {/*
            New Task:
            - flex-1 on mobile  → fills remaining space (or wraps full-width when tight)
            - flex-none on sm+  → natural size next to filters
            - min-w-[76px]      → prevents the button from collapsing below readable width
          */}
          <Button
            onClick={onNewTask}
            variant="primary"
            className="flex-1 sm:flex-none min-w-[76px] sm:min-w-0 justify-center whitespace-nowrap"
          >
            {t("module.newTask")}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map((tab) => {
          const isActive = tab === activeTab;
          return (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                isActive
                  ? "text-primary border-primary"
                  : "text-gray-500 border-transparent hover:text-dark-900"
              }`}
            >
              {t(`list.tabs.${tab}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
